import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Mode client — l'électricien retourne son écran vers son client.
 *
 * Ce n'est pas un compte différent ni une session différente : c'est la MÊME
 * session, le MÊME panier, simplement une présentation dépouillée de tout ce
 * qui regarde l'électricien et pas son client (paliers de remise, commission,
 * portefeuille, notifications, navigation métier).
 *
 * Le mode vit dans l'URL plutôt que dans un état React : un rafraîchissement
 * ne fait pas retomber l'écran en mode électricien devant le client, et le
 * bouton Retour du navigateur reste cohérent.
 *
 * `/mode-client` et non `/client` : `/clients` (la page métier) existe déjà,
 * un préfixe `/client` rendrait la détection ambiguë.
 */
export const CLIENT_MODE_ROOT = '/mode-client';

export const CLIENT_MODE_ROUTES = {
  shop: CLIENT_MODE_ROOT,
  catalogue: `${CLIENT_MODE_ROOT}/catalogue`,
  checkout: `${CLIENT_MODE_ROOT}/checkout`,
} as const;

export function isClientModePath(pathname: string): boolean {
  return (
    pathname === CLIENT_MODE_ROOT || pathname.startsWith(`${CLIENT_MODE_ROOT}/`)
  );
}

/** `true` dès que l'écran courant est présenté au client. */
export function useClientMode(): boolean {
  const { pathname } = useLocation();
  return isClientModePath(pathname);
}

/**
 * Préfixe une route pour rester dans le mode courant.
 *
 * Les surfaces partagées (panier, checkout) naviguent via ce helper : en mode
 * électricien `/checkout`, en mode client `/mode-client/checkout` — sans quoi
 * un clic sur « Passer commande » ferait sortir du mode client devant le
 * client, en réaffichant la sidebar et la commission.
 */
export function useModePath(): (path: string) => string {
  const clientMode = useClientMode();
  return useCallback(
    (path: string) => {
      if (!clientMode) return path;
      if (path === '/home') return CLIENT_MODE_ROUTES.shop;
      return `${CLIENT_MODE_ROOT}${path}`;
    },
    [clientMode],
  );
}
